import React, { FC, useState } from "react";

import Typography from "../../components/Typography/Typography";
import Button from "../../components/Button/Button";
import ASSETS from "../../assets";
import InfoItem from "./InfoItem";
import { IAttessation, IUser } from "../../types/interfaces";
import DocPreview from "../../components/DocPreview/DocPreview";
import DateSelector from "../../components/DateSelector/DateSelector";
import dayjs from "dayjs";
import CompareAttestation from "../../dialogs/CompareAttestation/CompareAttestation";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { hexToString } from "../../utils";

interface IProps {
  attestaion: IAttessation;
  allAttestations?: IAttessation[];
  isMyAttestation?: boolean;
  isOtherUser?: boolean;
  user: IUser | null;
  latestUID: string;
}

const InspectDocDetails: FC<IProps> = ({
  attestaion,
  allAttestations,
  isOtherUser,
  latestUID,
}) => {
  const [selectedDate, setSelectedDate] = useState(
    dayjs.unix(+attestaion.time).utc().format("MMM DD,YYYY @ hh:mm A")
  );
  const [currentAttestation, setCurrentAttestation] =
    useState<IAttessation>(attestaion);
  const { user } = useSelector((state: RootState) => state.auth);
  const [open, setOpen] = useState(false);
  const handleCompare = () => {
    setOpen(true);
  };

  const handleOpenFile = (hideToolbar = false) => {
    const fileUrl: any = currentAttestation.document;
    const finalUrl = hideToolbar ? fileUrl : `${fileUrl}#toolbar=0`;
    window.open(finalUrl, "_blank");
  };
  return (
    <div className="flex justify-between gap-12 flex-grow">
      <div className="relative w-[502px] min-w-[300px]  rounded-lg border border-k-border bg-k-grey-200">
        {currentAttestation.document && (
          <DocPreview
            url={currentAttestation.document}
            docType={currentAttestation.documentType === "pdf" ? "pdf" : "word"}
          />
        )}
      </div>

      <div className="flex flex-col gap-4 min-w-[328px]">
        <Typography
          variant="h3"
          color="black"
          className="font-normal line-clamp-1"
        >
          {currentAttestation.name}
        </Typography>
        {isOtherUser && (
          <div className="flex flex-row gap-[10px]">
            <div className="relative w-[35px] h-[35px] flex items-center justify-center rounded-full bg-k-sky-blue-light text-black text-base semibold border-2 uppercase border-k-sky-blue">
              {currentAttestation?.email?.slice(0, 2)}
              {currentAttestation?.verificationLevel &&
                currentAttestation.verificationLevel !== "0x00" &&
                hexToString(currentAttestation.verificationLevel) ===
                  "device" && (
                  <img
                    src={ASSETS.onchain_verified}
                    alt="verified"
                    className="absolute -bottom-1 -right-1 h-4 w-4"
                  />
                )}
            </div>
            <div className="flex flex-col">
              <Typography variant="body1" color="black" className="semibold">
                {currentAttestation.email}
              </Typography>
              <Typography variant="body1" color="grey" className="font-medium">
                Owner
              </Typography>
            </div>
          </div>
        )}

        <div>
          <DateSelector
            attestations={allAttestations}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            setCurrentAttestation={setCurrentAttestation}
            customWidth="100%"
            latestUID={latestUID}
          />
        </div>

        <div className="flex gap-4">
          <div className="flex flex-col gap-4">
            <div>
              <Typography variant="body1" color="grey" className="font-medium">
                Original Date Created:
              </Typography>
              <Typography
                variant="body1"
                color="black"
                className="semibold break-all"
              >
                {/* {dayjs
                  .unix(+currentAttestation.time)
                  .utc()
                  .format('MMM DD,YYYY @ hh:mm A')} */}
                {currentAttestation?.time &&
                  dayjs
                    .unix(+currentAttestation.time)
                    .format("MMM DD,YYYY @ hh:mm A")}
              </Typography>
            </div>
            <InfoItem
              label="UID:"
              value={currentAttestation.UID}
              icon={ASSETS.copy_grey}
            />
            <div className="flex w-full flex-col">
              <Typography variant="body1" color="grey" className="font-medium">
                Record Location:
              </Typography>
              <Typography
                variant="body1"
                className="flex items-center gap-2 semibold"
              >
                <img
                  src={
                    currentAttestation.type === "On Chain"
                      ? ASSETS.onchain
                      : ASSETS.offchain
                  }
                  alt=""
                  className="w-4 h-4"
                />
                {currentAttestation.type === "On Chain"
                  ? "On-Chain"
                  : "Off-Chain"}
              </Typography>
            </div>
            <InfoItem
              label="File Name:"
              value={currentAttestation.document || ""}
            />
          </div>

          <div className="flex flex-col gap-4">
            <InfoItem
              label="Your Wallet Address:"
              value={currentAttestation.creator}
              icon={ASSETS.copy_grey}
            />
            <InfoItem
              label="Doc Hash:"
              value={currentAttestation.docHash}
              icon={ASSETS.copy_grey}
            />
            <div>
              <div className="flex items-center justify-between">
                {/* <Typography variant='body1' color='grey'>
            Status:
          </Typography> */}
                <div className="flex items-center gap-1.5 p-1 mb-1 rounded-md border border-[#ADD2FD] bg-[#D4EBFF]">
                  {(() => {
                    const isVerified = currentAttestation.status === true;

                    return (
                      <>
                        {isVerified && (
                          <img src={ASSETS.offchain_verified} alt="" />
                        )}
                        {!isVerified && <img src={ASSETS.alert_red} alt="" />}
                        <Typography variant="body1" className="font-bold">
                          {isVerified ? "World ID Verified" : "Unverified"}
                        </Typography>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 mt-7">
          {user?.walletAddress !== currentAttestation.creator && (
            <Button
              leftIcon={ASSETS.upload}
              label="Verify Document"
              variant="primary"
              className="w-full mt-3"
              onClick={handleCompare}
            />
          )}
          <Button
            rightIcon={ASSETS.arrow_up}
            label="Open File In New Window"
            variant="outlined"
            className="w-full h-[35px] text-sm"
            onClick={() => handleOpenFile(false)}
          />
          <Button
            // leftIcon={ASSETS.copy_black}
            label="Print File"
            variant="outlined"
            className="w-full h-[35px] text-sm"
            onClick={() => handleOpenFile(true)}
          />
        </div>
      </div>
      {open && currentAttestation && (
        <CompareAttestation
          open={open}
          setOpen={setOpen}
          docHash={currentAttestation?.docHash}
        />
      )}
    </div>
  );
};

export default InspectDocDetails;
